import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Tag } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useStore } from '../store/useStore';

interface TagModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  tags: string[];
}

export const TagModal: React.FC<TagModalProps> = ({ isOpen, onClose, ticketId, tags }) => {
  const { addTicketTag, removeTicketTag, ticketTagCatalog } = useStore();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const handleAdd = (tag: string) => {
    const t = tag.trim();
    if (t && !tags.includes(t)) {
      void addTicketTag(ticketId, t);
      setValue('');
    }
  };

  const filtered = ticketTagCatalog.map((tag) => tag.label)
    .filter(s => !tags.includes(s))
    .filter(s => !value || s.toLowerCase().includes(value.toLowerCase()));

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black/32 backdrop-blur-[2px] z-[100]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 32 }}
            transition={{ type: 'spring', damping: 30, stiffness: 400, mass: 0.7 }}
            className="fixed z-[101] bottom-0 left-0 right-0 md:inset-0 md:flex md:items-center md:justify-center pointer-events-none"
          >
            <div
              className="bg-white rounded-t-[24px] md:rounded-[24px] w-full md:w-[380px] shadow-[0_-4px_40px_rgba(0,0,0,0.18)] pointer-events-auto overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="md:hidden flex justify-center pt-3 pb-2">
                <div className="w-10 h-[4px] rounded-full bg-[#D1D1D6]" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#F2F2F7]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-[10px] bg-[#007AFF]/10 flex items-center justify-center">
                    <Tag size={16} className="text-[#007AFF]" strokeWidth={2} />
                  </div>
                  <span className="text-[17px] font-semibold text-black tracking-[-0.2px]">Теги тикета</span>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-[#F2F2F7] flex items-center justify-center text-[#8E8E93] hover:bg-[#E5E5EA] transition-colors"
                >
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>

              <div className="px-5 py-5 space-y-5">
                {/* Current tags */}
                {tags.length > 0 && (
                  <div>
                    <p className="text-[12px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-3">Текущие теги ({tags.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map(tag => (
                        <div key={tag} className="flex items-center gap-1.5 bg-[#F2F2F7] rounded-full pl-3 pr-2 py-1.5 border border-black/5">
                          <span className="text-[14px] text-black font-medium">{tag}</span>
                          <button
                            onClick={() => { void removeTicketTag(ticketId, tag); }}
                            className="w-[18px] h-[18px] rounded-full bg-[#C7C7CC]/40 hover:bg-[#FF3B30]/20 flex items-center justify-center transition-colors group"
                          >
                            <X size={10} className="text-[#8E8E93] group-hover:text-[#FF3B30] transition-colors" strokeWidth={2.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add input */}
                <div>
                  <p className="text-[12px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-3">Добавить тег</p>
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      value={value}
                      onChange={e => setValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); handleAdd(value); }
                        if (e.key === 'Escape') onClose();
                      }}
                      placeholder="Название тега..."
                      className="flex-1 h-[46px] bg-[#F2F2F7] rounded-[12px] px-4 text-[15px] text-black placeholder:text-[#C7C7CC] outline-none border-2 border-transparent focus:border-[#007AFF]/40 focus:bg-white transition-all"
                    />
                    <button
                      onClick={() => handleAdd(value)}
                      disabled={!value.trim() || tags.includes(value.trim())}
                      className="h-[46px] w-[46px] bg-[#007AFF] text-white rounded-[12px] flex items-center justify-center disabled:opacity-35 transition-all active:scale-95 shadow-[0_2px_8px_rgba(0,122,255,0.3)] disabled:shadow-none"
                    >
                      <Plus size={20} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                {/* Quick pick */}
                {filtered.length > 0 && (
                  <div>
                    <p className="text-[12px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-3">Быстрый выбор</p>
                    <div className="flex flex-wrap gap-2">
                      {filtered.slice(0, 8).map(s => (
                        <button
                          key={s}
                          onClick={() => handleAdd(s)}
                          className="flex items-center gap-1.5 bg-[#007AFF]/8 text-[#007AFF] rounded-full pl-2.5 pr-3 py-1.5 text-[13px] font-medium hover:bg-[#007AFF]/15 transition-colors active:scale-95"
                        >
                          <Plus size={12} strokeWidth={2.5} />
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 pb-[var(--mobile-sheet-safe-bottom)] md:pb-5 pt-1">
                <button
                  onClick={onClose}
                  className="w-full bg-[#007AFF] hover:bg-[#0066DD] active:scale-[0.98] text-white text-[16px] font-semibold py-[14px] rounded-[14px] transition-all duration-150"
                >
                  Готово
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
